import { create } from 'zustand';
import type { ThreadDTO } from '@clanfire/shared';
import { threadApi } from '@/api/endpoints';
import { useReadStates } from './social';

/**
 * Threads the client knows about, by id. A channel's threads are loaded when
 * someone opens its thread list; threads also arrive on the messages they
 * were started from and through socket events.
 */
interface ThreadsState {
  threads: Record<string, ThreadDTO>;
  /** Whether a channel's full thread list has been loaded. */
  lists: Record<string, 'loading' | 'loaded' | 'error'>;
  upsert: (threads: ThreadDTO[]) => void;
  remove: (threadId: string) => void;
  removeChannel: (parentId: string) => void;
  loadChannel: (parentId: string, force?: boolean) => Promise<void>;
  reset: () => void;
}

export const useThreads = create<ThreadsState>((set, get) => ({
  threads: {},
  lists: {},
  upsert: (threads) =>
    set((s) => {
      if (threads.length === 0) return s;
      const next = { ...s.threads };
      for (const t of threads) next[t.id] = t;
      return { threads: next };
    }),
  remove: (threadId) =>
    set((s) => {
      const { [threadId]: _removed, ...threads } = s.threads;
      return { threads };
    }),
  removeChannel: (parentId) =>
    set((s) => ({
      threads: Object.fromEntries(Object.entries(s.threads).filter(([, t]) => t.parentId !== parentId)),
      lists: Object.fromEntries(Object.entries(s.lists).filter(([id]) => id !== parentId)),
    })),
  loadChannel: async (parentId, force = false) => {
    const status = get().lists[parentId];
    if (!force && (status === 'loading' || status === 'loaded')) return;
    set((s) => ({ lists: { ...s.lists, [parentId]: 'loading' } }));
    try {
      const { threads, readStates } = await threadApi.list(parentId);
      get().upsert(threads);
      for (const r of readStates) useReadStates.getState().markRead(r.channelId, r.lastReadAt);
      set((s) => ({ lists: { ...s.lists, [parentId]: 'loaded' } }));
    } catch {
      set((s) => ({ lists: { ...s.lists, [parentId]: 'error' } }));
    }
  },
  reset: () => set({ threads: {}, lists: {} }),
}));

export function useThread(threadId: string | null | undefined): ThreadDTO | undefined {
  return useThreads((s) => (threadId ? s.threads[threadId] : undefined));
}

/** Whether a thread has messages you haven't read. */
export function isThreadUnread(thread: ThreadDTO, lastReadAt: string | undefined): boolean {
  if (!thread.lastMessageAt || thread.messageCount === 0) return false;
  return thread.lastMessageAt > (lastReadAt ?? thread.createdAt);
}

export function useThreadUnread(thread: ThreadDTO | undefined): boolean {
  const lastReadAt = useReadStates((s) => (thread ? s.channels[thread.id]?.lastReadAt : undefined));
  return !!thread && isThreadUnread(thread, lastReadAt);
}

/** Where a thread opens: beside its channel. */
export const threadPath = (thread: Pick<ThreadDTO, 'id' | 'serverId' | 'parentId'>) => `/app/${thread.serverId}/${thread.parentId}/threads/${thread.id}`;
